import type { Catalog, Product, ProductView } from "./types";

export interface CatalogOverlayGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
  actionLeft: number;
  actionTop: number;
}

export interface CatalogProductMarkerGroup {
  id: string;
  products: ProductView[];
  boundingBox: [number, number, number, number];
  geometry: CatalogOverlayGeometry;
}

export interface CatalogMarkerAnchor {
  id: string;
  left: number;
  top: number;
}

export const CATALOG_ZOOM_MIN = 1;
export const CATALOG_ZOOM_MAX = 3;
export const CATALOG_ZOOM_BUTTON_SCALE = 2;

export function clampCatalogPage(page: number, pageCount: number): number {
  const maximum = Math.max(1, Math.trunc(pageCount));
  return Math.min(maximum, Math.max(1, Math.trunc(page) || 1));
}

export function catalogPageImage(
  catalog: Catalog,
  page: number,
): string | null {
  const normalizedPage = clampCatalogPage(page, catalog.pages);
  const path = catalog.page_images?.[normalizedPage - 1];
  return typeof path === "string" && path.length > 0 ? path : null;
}

/** A shared snapshot deliberately has no page images. Bind by exact catalog ID,
 * never by a store/title (which could silently open a different week's issue). */
export function productCatalogPage(
  product: ProductView,
  catalogs: readonly Catalog[] = [],
): Catalog | null {
  if (product.catalog_id !== product.catalog.id) return null;
  const catalog = catalogs.find(candidate => candidate.id === product.catalog_id) ?? product.catalog;
  // Unlike the viewer's navigation, this reference must not clamp a missing
  // product page to some other page and claim that it contains the product.
  if (!Number.isInteger(product.page) || product.page < 1 || product.page > catalog.pages) return null;
  return catalogPageImage(catalog, product.page) ? catalog : null;
}

export function productViewForCatalog(
  product: Product,
  catalog: Catalog,
): ProductView {
  return { ...product, catalog };
}

export function catalogProductsOnPage(
  catalog: Catalog,
  page: number,
): ProductView[] {
  const normalizedPage = clampCatalogPage(page, catalog.pages);
  return catalog.products
    .filter((product) => product.page === normalizedPage)
    .map((product) => productViewForCatalog(product, catalog));
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

export function catalogOverlayGeometry(
  boundingBox: readonly [number, number, number, number],
): CatalogOverlayGeometry {
  const [ymin, xmin, ymax, xmax] = boundingBox;
  const left = clampPercent(xmin / 10);
  const top = clampPercent(ymin / 10);
  const right = clampPercent(xmax / 10);
  const bottom = clampPercent(ymax / 10);
  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
    actionLeft: Math.min(97, Math.max(3, right - 1.5)),
    actionTop: Math.min(97, Math.max(3, top + 1.5)),
  };
}

function productMarkerKey(product: ProductView): string {
  return `${product.catalog_id}:${product.id}`;
}

function boxArea(
  [ymin, xmin, ymax, xmax]: readonly [number, number, number, number],
): number {
  return Math.max(0, ymax - ymin) * Math.max(0, xmax - xmin);
}

function intersectionOverSmallerBox(
  first: readonly [number, number, number, number],
  second: readonly [number, number, number, number],
): number {
  const intersectionHeight = Math.max(
    0,
    Math.min(first[2], second[2]) - Math.max(first[0], second[0]),
  );
  const intersectionWidth = Math.max(
    0,
    Math.min(first[3], second[3]) - Math.max(first[1], second[1]),
  );
  const smallerArea = Math.min(boxArea(first), boxArea(second));
  return smallerArea > 0
    ? (intersectionHeight * intersectionWidth) / smallerArea
    : 0;
}

/**
 * Products emitted from the same printed offer can legitimately share a box
 * (for example, two package sizes with different prices). They need one
 * marker with a variant chooser because separate markers would overlap.
 */
export function shouldGroupCatalogProducts(
  first: ProductView,
  second: ProductView,
): boolean {
  const firstGeometry = catalogOverlayGeometry(first.bounding_box);
  const secondGeometry = catalogOverlayGeometry(second.bounding_box);
  const markerDistanceX = Math.abs(
    firstGeometry.actionLeft - secondGeometry.actionLeft,
  );
  const markerDistanceY = Math.abs(
    firstGeometry.actionTop - secondGeometry.actionTop,
  );
  if (markerDistanceX < 0.05 && markerDistanceY < 0.05) {
    return true;
  }
  const maximumCoordinateDelta = Math.max(
    Math.abs(first.bounding_box[0] - second.bounding_box[0]),
    Math.abs(first.bounding_box[1] - second.bounding_box[1]),
    Math.abs(first.bounding_box[2] - second.bounding_box[2]),
    Math.abs(first.bounding_box[3] - second.bounding_box[3]),
  );
  if (maximumCoordinateDelta <= 20) {
    return true;
  }
  return (
    markerDistanceX <= 1.5 &&
    markerDistanceY <= 1.5 &&
    intersectionOverSmallerBox(first.bounding_box, second.bounding_box) >= 0.72
  );
}

function unionBoundingBoxes(
  products: readonly ProductView[],
): [number, number, number, number] {
  return [
    Math.min(...products.map((product) => product.bounding_box[0])),
    Math.min(...products.map((product) => product.bounding_box[1])),
    Math.max(...products.map((product) => product.bounding_box[2])),
    Math.max(...products.map((product) => product.bounding_box[3])),
  ];
}

export function catalogProductMarkerGroups(
  catalog: Catalog,
  page: number,
): CatalogProductMarkerGroup[] {
  const products = catalogProductsOnPage(catalog, page);
  const parents = products.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parents[root] !== root) {
      root = parents[root]!;
    }
    while (parents[index] !== index) {
      const next = parents[index]!;
      parents[index] = root;
      index = next;
    }
    return root;
  };
  const union = (first: number, second: number): void => {
    const firstRoot = find(first);
    const secondRoot = find(second);
    if (firstRoot !== secondRoot) {
      parents[secondRoot] = firstRoot;
    }
  };
  for (let first = 0; first < products.length; first += 1) {
    for (let second = first + 1; second < products.length; second += 1) {
      if (
        shouldGroupCatalogProducts(products[first]!, products[second]!)
      ) {
        union(first, second);
      }
    }
  }
  const grouped = new Map<number, ProductView[]>();
  products.forEach((product, index) => {
    const root = find(index);
    const members = grouped.get(root) ?? [];
    members.push(product);
    grouped.set(root, members);
  });
  return [...grouped.values()].map((members) => {
    const boundingBox = unionBoundingBoxes(members);
    return {
      id: members.map(productMarkerKey).join("|"),
      products: members,
      boundingBox,
      geometry: catalogOverlayGeometry(boundingBox),
    };
  });
}

/**
 * Separates marker hit areas using the actual rendered page dimensions. This
 * keeps distinct neighbouring offers individually tappable on narrow phones.
 */
export function separateCatalogMarkerAnchors(
  anchors: readonly CatalogMarkerAnchor[],
  canvasWidth: number,
  canvasHeight: number,
  markerDiameter = 34,
): CatalogMarkerAnchor[] {
  const width = Math.max(markerDiameter, canvasWidth);
  const height = Math.max(markerDiameter, canvasHeight);
  const radius = markerDiameter / 2;
  const step = markerDiameter + 4;
  const offsets: Array<readonly [number, number]> = [[0, 0]];
  for (let ring = 1; ring <= 4; ring += 1) {
    const distance = ring * step;
    offsets.push(
      [distance, 0],
      [-distance, 0],
      [0, distance],
      [0, -distance],
      [distance, distance],
      [-distance, distance],
      [distance, -distance],
      [-distance, -distance],
    );
  }
  const placed: Array<{ x: number; y: number }> = [];
  return anchors.map((anchor) => {
    const baseX = (anchor.left / 100) * width;
    const baseY = (anchor.top / 100) * height;
    let selectedX = Math.min(width - radius, Math.max(radius, baseX));
    let selectedY = Math.min(height - radius, Math.max(radius, baseY));
    for (const [offsetX, offsetY] of offsets) {
      const candidateX = Math.min(
        width - radius,
        Math.max(radius, baseX + offsetX),
      );
      const candidateY = Math.min(
        height - radius,
        Math.max(radius, baseY + offsetY),
      );
      const collides = placed.some(
        (candidate) =>
          Math.abs(candidate.x - candidateX) < markerDiameter &&
          Math.abs(candidate.y - candidateY) < markerDiameter,
      );
      if (!collides) {
        selectedX = candidateX;
        selectedY = candidateY;
        break;
      }
    }
    placed.push({ x: selectedX, y: selectedY });
    return {
      id: anchor.id,
      left: (selectedX / width) * 100,
      top: (selectedY / height) * 100,
    };
  });
}

export function clampCatalogZoomScale(scale: number): number {
  if (!Number.isFinite(scale)) {
    return CATALOG_ZOOM_MIN;
  }
  return Math.min(CATALOG_ZOOM_MAX, Math.max(CATALOG_ZOOM_MIN, scale));
}

export function catalogPinchZoomScale(
  startingScale: number,
  startingDistance: number,
  currentDistance: number,
): number {
  if (startingDistance <= 0 || !Number.isFinite(currentDistance)) {
    return clampCatalogZoomScale(startingScale);
  }
  return clampCatalogZoomScale(
    startingScale * (currentDistance / startingDistance),
  );
}
